import {
  ArrowRight,
  BadgeDollarSign,
  Briefcase,
  ChevronRight,
  Clock,
  FileCheck,
  FileText,
  Inbox,
  LayoutDashboard,
  Scale,
  Send,
  Star,
  UserPlus,
} from 'lucide-react';
import { motion } from 'motion/react';

import { useI18n } from '../../i18n';

function HeroSection() {
  const { t } = useI18n();

  return (
    <section className="relative overflow-hidden bg-[#002711] px-4 pt-40 pb-24 sm:px-6 sm:pt-52 sm:pb-32 md:px-8 lg:px-32">
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]">
        <div
          className="h-full w-full"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,166,62,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,166,62,0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <motion.div
        className="pointer-events-none absolute start-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00a63e]/10 blur-3xl"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 2, ease: 'easeOut' }}
      />

      <div className="relative z-10 mx-auto max-w-4xl text-center">
        <motion.h1
          className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          {t.serviceProvider.hero.title}
        </motion.h1>

        <motion.p
          className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/70 sm:text-xl md:text-2xl"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8, ease: 'easeOut' }}
        >
          {t.serviceProvider.hero.subtitle}
        </motion.p>

        <motion.div
          className="mt-10"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.7, ease: 'easeOut' }}
        >
          <motion.a
            href="https://cavendor-develop.web.app"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-[#00a63e] px-8 py-4 text-lg font-bold text-white transition-colors"
            whileHover={{
              scale: 1.05,
              backgroundColor: '#00bf47',
              boxShadow:
                '0 0 40px rgba(0, 166, 62, 0.4), 0 0 80px rgba(0, 166, 62, 0.2)',
            }}
            whileTap={{ scale: 0.97 }}
          >
            {t.serviceProvider.hero.cta}
            <ArrowRight className="h-5 w-5 rtl:-scale-x-100" />
          </motion.a>
        </motion.div>
      </div>

      <div className="absolute start-0 end-0 bottom-0 h-24 bg-linear-to-t from-gray-50 to-transparent" />
    </section>
  );
}

function WhyJoinSection() {
  const { t } = useI18n();

  const features = [
    {
      icon: Scale,
      ...t.serviceProvider.whyJoin.features.fairCompetition,
    },
    {
      icon: Briefcase,
      ...t.serviceProvider.whyJoin.features.newOpportunities,
    },
    {
      icon: BadgeDollarSign,
      ...t.serviceProvider.whyJoin.features.guaranteedPayments,
    },
    {
      icon: LayoutDashboard,
      ...t.serviceProvider.whyJoin.features.easyManagement,
    },
    {
      icon: Star,
      ...t.serviceProvider.whyJoin.features.professionalReputation,
    },
  ];

  return (
    <section className="bg-gray-50 px-4 py-16 sm:px-6 sm:py-20 md:px-8 md:py-24 lg:px-32">
      <motion.div
        className="mb-14 text-center"
        initial={{ y: 40, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        viewport={{ once: true }}
      >
        <div className="mb-3 flex items-center justify-center gap-2">
          <div className="h-px w-8 bg-[#00a63e]" />
          <div className="h-2 w-2 rounded-full bg-[#00a63e]" />
          <div className="h-px w-8 bg-[#00a63e]" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
          {t.serviceProvider.whyJoin.title}
        </h2>
      </motion.div>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <motion.div
              key={index}
              className={`group relative overflow-hidden rounded-2xl border border-white/60 bg-white/70 p-8 backdrop-blur-sm ${
                index === features.length - 1
                  ? 'sm:col-span-2 sm:mx-auto sm:max-w-md lg:col-span-1 lg:mx-0 lg:max-w-none'
                  : ''
              }`}
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{
                delay: 0.1 + index * 0.1,
                duration: 0.6,
                ease: 'easeOut',
              }}
              viewport={{ once: true }}
              whileHover={{
                y: -4,
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
                transition: { duration: 0.3 },
              }}
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-[#00a63e]" />

              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-[#00a63e]/10 transition-colors group-hover:bg-[#00a63e]/15">
                <Icon className="h-7 w-7 text-[#00a63e]" strokeWidth={1.5} />
              </div>

              <h3 className="mb-2 text-lg font-bold text-gray-900">
                {feature.title}
              </h3>
              <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                {feature.description}
              </p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function HowToStartSection() {
  const { t } = useI18n();

  const stepIcons = [UserPlus, FileCheck, Clock, Inbox, Send];

  return (
    <section className="bg-white px-4 py-16 sm:px-6 sm:py-20 md:px-8 md:py-24 lg:px-32">
      <motion.div
        className="mb-16 text-center"
        initial={{ y: 40, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        viewport={{ once: true }}
      >
        <div className="mb-3 flex items-center justify-center gap-2">
          <div className="h-px w-8 bg-[#00a63e]" />
          <div className="h-2 w-2 rounded-full bg-[#00a63e]" />
          <div className="h-px w-8 bg-[#00a63e]" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
          {t.serviceProvider.howToStart.title}
        </h2>
      </motion.div>

      <div className="mx-auto max-w-3xl">
        <div className="relative">
          <motion.div
            className="absolute start-6 top-0 bottom-0 w-0.5 bg-[#00a63e] sm:start-8"
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            viewport={{ once: true }}
            style={{ originY: 0 }}
          />

          <div className="flex flex-col gap-10">
            {t.serviceProvider.howToStart.steps.map((step, index) => {
              const Icon = stepIcons[index];
              return (
                <motion.div
                  key={index}
                  className="relative flex items-start gap-6"
                  initial={{ x: -30, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{
                    delay: 0.15 + index * 0.12,
                    duration: 0.6,
                    ease: 'easeOut',
                  }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-[#00a63e] bg-white sm:h-16 sm:w-16"
                    whileHover={{
                      scale: 1.1,
                      backgroundColor: '#00a63e',
                      color: '#ffffff',
                      transition: { duration: 0.2 },
                    }}
                  >
                    <span className="text-lg font-bold text-[#00a63e] sm:text-xl">
                      {index + 1}
                    </span>
                  </motion.div>

                  <div className="flex-1 rounded-xl border border-gray-100 bg-gray-50/50 p-5 sm:p-6">
                    <div className="mb-2 flex items-center gap-3">
                      {Icon && (
                        <Icon
                          className="h-5 w-5 text-[#00a63e]"
                          strokeWidth={1.5}
                        />
                      )}
                      <h3 className="text-lg font-bold text-gray-900">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function RequiredDocumentsSection() {
  const { t } = useI18n();

  return (
    <section className="bg-white px-4 py-16 sm:px-6 sm:py-20 md:px-8 md:py-24 lg:px-32">
      <div className="mx-auto max-w-3xl">
        <motion.div
          className="mb-12 text-center"
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <div className="mb-3 flex items-center justify-center gap-3">
            <div className="h-px w-12 bg-[#00a63e]" />
            <FileText className="h-6 w-6 text-[#00a63e]" />
            <div className="h-px w-12 bg-[#00a63e]" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            {t.serviceProvider.requiredDocuments.title}
          </h2>
        </motion.div>

        <div className="flex flex-col gap-4">
          {t.serviceProvider.requiredDocuments.items.map((item, index) => (
            <motion.div
              key={index}
              className="group flex items-center gap-4 rounded-xl border border-gray-100 bg-gray-50 p-5 transition-colors hover:border-[#00a63e]/30 hover:bg-gray-100"
              initial={{ x: -30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{
                delay: 0.1 + index * 0.1,
                duration: 0.5,
                ease: 'easeOut',
              }}
              viewport={{ once: true }}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#00a63e]/10 text-sm font-bold text-[#00a63e]">
                {(index + 1).toString().padStart(2, '0')}
              </div>

              <span className="flex-1 text-base font-medium text-gray-900 sm:text-lg">
                {item}
              </span>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-14 text-center"
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          viewport={{ once: true }}
        >
          <motion.a
            href="https://cavendor-develop.web.app"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-[#00a63e] px-8 py-4 text-lg font-bold text-white transition-colors"
            whileHover={{
              scale: 1.05,
              backgroundColor: '#00bf47',
              boxShadow:
                '0 0 40px rgba(0, 166, 62, 0.4), 0 0 80px rgba(0, 166, 62, 0.2)',
            }}
            whileTap={{ scale: 0.97 }}
          >
            {t.serviceProvider.hero.cta}
            <ArrowRight className="h-5 w-5 rtl:-scale-x-100" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}

export default function ServiceProviderPage() {
  return (
    <>
      <HeroSection />
      <WhyJoinSection />
      <HowToStartSection />
      <RequiredDocumentsSection />
    </>
  );
}
